import React, { useState, useEffect } from "react";
import { useParams, useNavigate } from "react-router-dom";
import axios from "axios";
import SidBarClient from "../SideBarClient";
import DetailCommandeService from "../../../../Services/Admin/DetailCommandeService";

export default function RembourserClient() {
  const { id, orderId } = useParams<{ id: string; orderId: string }>();
  const navigate = useNavigate();
  const [articles, setArticles] = useState<any[]>([]);
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const [selectedIdx, setSelectedIdx] = useState<number>(0);
  const [quantity, setQuantity] = useState(1);
  const [note, setNote] = useState("");
  const [error, setError] = useState("");
  const [isMobile, setIsMobile] = useState(window.innerWidth < 768);

  useEffect(() => {
    const handleResize = () => setIsMobile(window.innerWidth < 768);
    window.addEventListener("resize", handleResize);
    return () => window.removeEventListener("resize", handleResize);
  }, []);

  // Load order line items — show ALL articles (even confirmed)
  useEffect(() => {
    const fetchArticles = async () => {
      try {
        setLoading(true);
        const res = await DetailCommandeService(orderId).getCommandeDetailsAdmin();
        // Accept any articles that are not already being reimbursed or cancelled
        const all = Array.isArray(res.data) ? res.data : [];
        const eligible = all.filter((a: any) => {
          const s = (a.Statut ?? "").toLowerCase();
          return !s.includes("annul") && !s.includes("rembours");
        });
        setArticles(eligible);
        if (eligible.length > 0) setSelectedIdx(0);
      } catch {
        setArticles([]);
      } finally {
        setLoading(false);
      }
    };
    fetchArticles();
  }, [orderId]);

  const selectedArticle = articles[selectedIdx];
  const maxQty = selectedArticle ? (Number(selectedArticle.quantity) || 1) : 1;

  const handleQtyChange = (val: number) => {
    if (val < 1) setQuantity(1);
    else if (val > maxQty) setQuantity(maxQty);
    else setQuantity(val);
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!selectedArticle) {
      setError("Veuillez sélectionner un article.");
      return;
    }
    setError("");
    setSubmitting(true);
    try {
      await axios.post(
        `${process.env.REACT_APP_PHP_APP_URL}/client/${id}/demande-remboursement`,
        {
          articleName: selectedArticle.NomArticle || selectedArticle.Designation || "Article",
          refArticle:  selectedArticle.RefArticle || "",
          quantity,
          note,
          orderId,
        }
      );
      setSubmitted(true);
    } catch {
      setError("Une erreur est survenue. Veuillez réessayer.");
    } finally {
      setSubmitting(false);
    }
  };

  // ── Styles ──────────────────────────────────────────────────────────────────
  const card: React.CSSProperties = {
    backgroundColor: "#fff",
    padding: isMobile ? "24px" : "40px",
    borderRadius: "16px",
    boxShadow: "0 10px 30px rgba(15,23,42,0.06)",
    border: "1px solid #E2E8F0",
  };
  const label: React.CSSProperties = {
    fontWeight: 600, color: "#334155", display: "block", marginBottom: "8px", fontSize: "14px",
  };
  const input: React.CSSProperties = {
    width: "100%", padding: "12px 16px", borderRadius: "10px",
    border: "1.5px solid #E2E8F0", fontWeight: 500, color: "#0F172A",
    boxShadow: "none", outline: "none", fontSize: "14px", boxSizing: "border-box",
  };

  return (
    <>
      <SidBarClient />
      <div style={{ paddingLeft: isMobile ? "0" : "80px", minHeight: "100vh", backgroundColor: "#F8FAFC" }}>
        <div style={{ maxWidth: "820px", margin: "0 auto", padding: isMobile ? "80px 16px 40px" : "50px 24px 60px" }}>

          {/* Back button + title */}
          <div style={{ display: "flex", alignItems: "center", gap: "16px", marginBottom: "32px" }}>
            <button
              onClick={() => window.history.back()}
              style={{
                width: "40px", height: "40px", borderRadius: "12px", border: "1.5px solid #E2E8F0",
                backgroundColor: "#F8FAFC", display: "flex", alignItems: "center", justifyContent: "center",
                cursor: "pointer", color: "#64748B", transition: "all 0.2s",
              }}
              onMouseOver={(e) => { e.currentTarget.style.borderColor = "#F5A623"; e.currentTarget.style.color = "#F5A623"; }}
              onMouseOut={(e) => { e.currentTarget.style.borderColor = "#E2E8F0"; e.currentTarget.style.color = "#64748B"; }}
            >
              <i className="bi bi-arrow-left-short" style={{ fontSize: "22px" }} />
            </button>
            <div>
              <h2 style={{ fontSize: isMobile ? "20px" : "24px", fontWeight: 800, color: "#0F172A", margin: 0 }}>
                Demande de remboursement
              </h2>
              <p style={{ color: "#64748B", fontSize: "13px", margin: "4px 0 0" }}>
                Commande <span style={{ fontWeight: 600, color: "#0F172A", fontFamily: "monospace" }}>#{orderId}</span>
              </p>
            </div>
          </div>

          <div style={card}>
            {/* Success state */}
            {submitted ? (
              <div style={{ textAlign: "center", padding: "40px 0" }}>
                <div style={{
                  width: "64px", height: "64px", borderRadius: "50%", backgroundColor: "#F0FDF4",
                  display: "flex", alignItems: "center", justifyContent: "center", margin: "0 auto 20px",
                  fontSize: "28px", color: "#16A34A",
                }}>
                  <i className="bi bi-check-circle-fill" />
                </div>
                <h4 style={{ fontWeight: 700, color: "#0F172A", fontSize: "20px", marginBottom: "8px" }}>
                  Demande envoyée !
                </h4>
                <p style={{ color: "#64748B", marginBottom: "28px", maxWidth: "340px", margin: "0 auto 28px" }}>
                  Votre demande de remboursement a été transmise au magasin. Vous serez contacté prochainement.
                </p>
                <button
                  onClick={() => navigate(`/clients/${id}/historique`)}
                  style={{
                    padding: "12px 28px", borderRadius: "10px", fontWeight: 600,
                    background: "linear-gradient(135deg,#F5A623,#E8930C)", color: "#fff",
                    border: "none", cursor: "pointer", fontSize: "14px",
                  }}
                >
                  Retour à l'historique
                </button>
              </div>
            ) : loading ? (
              /* Loading state */
              <div style={{ display: "flex", flexDirection: "column", alignItems: "center", padding: "60px 0", gap: "16px" }}>
                <div className="spinner-border" role="status" style={{ color: "#F5A623" }} />
                <p style={{ color: "#64748B", fontWeight: 500, margin: 0 }}>Chargement des articles…</p>
              </div>
            ) : submitting ? (
              /* Submitting state */
              <div style={{ textAlign: "center", padding: "60px 0", gap: "16px", display: "flex", flexDirection: "column", alignItems: "center" }}>
                <div className="spinner-border" role="status" style={{ color: "#F5A623", width: "3rem", height: "3rem" }} />
                <h4 style={{ color: "#0F172A", fontWeight: 600, fontSize: "18px", margin: 0 }}>Envoi en cours…</h4>
                <p style={{ color: "#64748B", margin: 0 }}>Veuillez patienter pendant le traitement de votre demande.</p>
              </div>
            ) : articles.length === 0 ? (
              /* No eligible articles */
              <div style={{ textAlign: "center", padding: "60px 0" }}>
                <div style={{
                  width: "64px", height: "64px", backgroundColor: "#FFFBEB", borderRadius: "50%",
                  display: "flex", alignItems: "center", justifyContent: "center", margin: "0 auto 16px",
                  color: "#F5A623", fontSize: "28px",
                }}>
                  <i className="bi bi-exclamation-triangle" />
                </div>
                <h4 style={{ color: "#0F172A", fontWeight: 600, fontSize: "18px" }}>Aucun article éligible</h4>
                <p style={{ color: "#64748B", maxWidth: "320px", margin: "8px auto 0" }}>
                  Tous les articles de cette commande sont déjà annulés ou remboursés.
                </p>
              </div>
            ) : (
              /* Form */
              <form onSubmit={handleSubmit}>
                {/* Article selector */}
                <div style={{ backgroundColor: "#F8FAFC", borderRadius: "12px", padding: "20px", marginBottom: "24px", border: "1px solid #E2E8F0" }}>
                  <h5 style={{ fontSize: "14px", fontWeight: 700, color: "#334155", marginBottom: "14px", display: "flex", alignItems: "center", gap: "8px" }}>
                    <i className="bi bi-box-seam" style={{ color: "#F5A623" }} /> Sélectionner l'article
                  </h5>
                  <select
                    value={selectedIdx}
                    onChange={(e) => { setSelectedIdx(Number(e.target.value)); setQuantity(1); }}
                    required
                    style={{ ...input, cursor: "pointer" }}
                  >
                    {articles.map((a: any, i: number) => (
                      <option key={i} value={i}>
                        {a.NomArticle || a.Designation || "Article"} — Réf. {a.RefArticle} (Qté: {a.quantity})
                      </option>
                    ))}
                  </select>

                  {selectedArticle && (
                    <div style={{ display: "flex", justifyContent: "space-between", backgroundColor: "#fff", padding: "14px 16px", borderRadius: "10px", border: "1px solid #E2E8F0", marginTop: "12px" }}>
                      <div>
                        <span style={{ fontSize: "11px", fontWeight: 700, color: "#94A3B8", textTransform: "uppercase", letterSpacing: "0.05em" }}>Référence</span>
                        <div style={{ fontWeight: 600, color: "#0F172A", fontFamily: "monospace", marginTop: "2px" }}>{selectedArticle.RefArticle}</div>
                      </div>
                      <div style={{ textAlign: "right" }}>
                        <span style={{ fontSize: "11px", fontWeight: 700, color: "#94A3B8", textTransform: "uppercase", letterSpacing: "0.05em" }}>Prix unit.</span>
                        <div style={{ fontWeight: 600, color: "#0F172A", fontFamily: "monospace", marginTop: "2px" }}>{selectedArticle.prix} MAD</div>
                      </div>
                    </div>
                  )}
                </div>

                {/* Quantity */}
                <div style={{ marginBottom: "24px" }}>
                  <label style={label}>Quantité à rembourser</label>
                  <div style={{ position: "relative", maxWidth: "200px" }}>
                    <input
                      type="number"
                      value={quantity}
                      onChange={(e) => handleQtyChange(Number(e.target.value))}
                      min={1}
                      max={maxQty}
                      required
                      style={input}
                    />
                    <span style={{ position: "absolute", right: "14px", top: "50%", transform: "translateY(-50%)", color: "#94A3B8", fontSize: "12px" }}>
                      / {maxQty}
                    </span>
                  </div>
                </div>

                {/* Note */}
                <div style={{ marginBottom: "28px" }}>
                  <label style={label}>Motif du remboursement</label>
                  <textarea
                    value={note}
                    onChange={(e) => setNote(e.target.value)}
                    placeholder="Expliquez la raison de votre demande…"
                    rows={4}
                    required
                    style={{ ...input, resize: "none", lineHeight: "1.5" }}
                  />
                </div>

                {error && (
                  <div style={{ backgroundColor: "#FEF2F2", border: "1px solid #FECACA", borderRadius: "8px", padding: "12px 16px", marginBottom: "20px", color: "#B91C1C", fontSize: "14px" }}>
                    {error}
                  </div>
                )}

                {/* Footer */}
                <div style={{ borderTop: "1px solid #E2E8F0", paddingTop: "24px", display: "flex", justifyContent: "space-between", alignItems: "center" }}>
                  <div>
                    <span style={{ fontSize: "12px", color: "#64748B" }}>Total estimé</span>
                    <div style={{ fontSize: "20px", fontWeight: 800, color: "#0F172A", fontFamily: "monospace" }}>
                      {selectedArticle ? (Number(selectedArticle.prix) * quantity).toFixed(2) : "0.00"}{" "}
                      <span style={{ fontSize: "13px", color: "#94A3B8", fontFamily: "sans-serif" }}>MAD</span>
                    </div>
                  </div>
                  <button
                    type="submit"
                    disabled={submitting}
                    style={{
                      padding: "12px 28px", borderRadius: "10px", fontWeight: 600,
                      background: submitting ? "#cbd5e1" : "linear-gradient(135deg,#F5A623,#E8930C)",
                      color: "#fff", border: "none", cursor: submitting ? "not-allowed" : "pointer",
                      display: "flex", alignItems: "center", gap: "8px", fontSize: "14px",
                      boxShadow: submitting ? "none" : "0 4px 14px rgba(245,166,35,0.3)",
                    }}
                  >
                    {submitting ? "Envoi…" : <>Soumettre <i className="bi bi-check-circle" /></>}
                  </button>
                </div>
              </form>
            )}
          </div>
        </div>
      </div>
    </>
  );
}
